// src/pages/transactions/TransactionReport.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowBack, Download, ZoomIn, ZoomOut } from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import { pdf } from "@react-pdf/renderer";
import TransactionInvoicePDF from "../../components/invoice/TransactionInvoicePDF"; 
import logo from "../../assets/images/logo-primary.png";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const TransactionReport = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.2);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get vendor data from Redux
  const vendorData = useSelector((state) => state.auth?.vendorData);

  const transactions = location.state?.transactions;
  const selectedDate = location.state?.date;

  useEffect(() => {
    if (transactions && transactions.length > 0) {
      generatePDF();
    }
  }, [transactions, vendorData]);

  const generatePDF = async () => {
    try {
      setLoading(true);
      const blob = await pdf(
        <TransactionInvoicePDF
          transactions={transactions}
          vendorData={vendorData}
          logoSrc={logo}
          selectedDate={selectedDate}
        />
      ).toBlob();
      setPdfBlob(blob);
      setLoading(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setLoading(false);
    }
  };

  if (!transactions || transactions.length === 0) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography variant="h6">No transactions found</Typography>
        <Button onClick={() => navigate("/transactions")} sx={{ ml: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 2.5));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  const handleDownload = () => {
    if (!pdfBlob) return;

    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    const filename = selectedDate
      ? `Transaction_Invoice_${selectedDate.replace(/ /g, "_")}_${vendorData?.name?.replace(/ /g, "_") || "Vendor"}.pdf`
      : `All_Transactions_Invoice_${vendorData?.name?.replace(/ /g, "_") || "Vendor"}.pdf`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          variant="outlined"
        >
          Back
        </Button>

        <Box display="flex" gap={1} alignItems="center">
          <IconButton onClick={handleZoomOut} size="small" disabled={loading}>
            <ZoomOut />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 60, textAlign: "center" }}>
            {Math.round(scale * 100)}%
          </Typography>
          <IconButton onClick={handleZoomIn} size="small" disabled={loading}>
            <ZoomIn />
          </IconButton>
          <Button
            startIcon={loading ? <CircularProgress size={16} /> : <Download />}
            onClick={handleDownload}
            variant="contained"
            disabled={loading}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      {/* PDF Viewer */}
      <Box
        sx={{
          flex: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.100",
          p: 2,
        }}
      >
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Generating invoice...</Typography>
          </Box>
        ) : pdfBlob ? (
          <Document
            file={pdfBlob}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            }
            error={
              <Box display="flex" justifyContent="center" p={4}>
                <Typography color="error">Failed to load PDF</Typography>
              </Box>
            }
          >
            {Array.from(new Array(numPages), (el, index) => (
              <Box key={`page_${index + 1}`} mb={2}>
                <Page
                  pageNumber={index + 1}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Box>
            ))}
          </Document>
        ) : (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography>Failed to generate invoice</Typography>
          </Box>
        )}
      </Box>

      {/* Page Info */}
      {numPages && !loading && (
        <Box mt={2} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Total Pages: {numPages} | Total Transactions: {transactions.length}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TransactionReport;
